import moment from 'moment';
import { ClientSubscription } from '../core/_models';

// WST-803 — service plan editing helpers (pure, unit-tested).

export type PlanEditUser = {
  provider?: { is_admin?: boolean; payment_details_enabled?: boolean } | null;
};

export type PlanChangeEntry = {
  field: string;
  from?: unknown;
  to?: unknown;
  changed_at?: string;
  changed_by?: string | null;
  effective_from?: string | null;
};

export const PLAN_CHANGE_FIELD_LABELS: Record<string, string> = {
  rate: 'Rate',
  payout: 'Payout',
  provider_service: 'Service',
  provider: 'Provider',
  service_quantity: 'Sessions per period',
  service_interval: 'Service interval',
  service_interval_count: 'Service interval length',
  subscription_interval: 'Billing interval',
  subscription_interval_count: 'Billing interval length',
  start_date: 'Start date',
  itemize_invoice: 'Itemized Invoicing',
  notify_on_payment: 'Payment Receipt Email',
  provider_note: 'Provider note',
  // WST-891: a per-service row edit is logged under the row field name, not
  // the plan mirror's `provider_service` (see apply_service_rows).
  service: 'Service',
};

/**
 * WST-891: the API keys a per-service row edit as "<field>[<service label>]"
 * (`apply_service_rows` in client/models.py) — there is no separate
 * service_name on the entry. "primary" is the synthetic label the serializer
 * uses for a legacy mirror-only service move, and names no real service — a
 * service literally NAMED "primary" would therefore be read as that marker and
 * lose its name from the label (the rest of the entry is unaffected).
 */
const splitChangeField = (field: string): { field: string; service?: string } => {
  const match = /^([^[]+)\[(.+)\]$/.exec(field);
  if (!match) return { field };
  return { field: match[1], service: match[2] === 'primary' ? undefined : match[2] };
};

// Every plan is editable (including new/invite-sent ones) except plans that
// have ended — canceled or voided.
const NON_EDITABLE_STATUSES = ['Canceled', 'Void'];

export const canEditPlan = (sub?: ClientSubscription, user?: PlanEditUser): boolean =>
  !!sub &&
  !NON_EDITABLE_STATUSES.includes(sub.status || '') &&
  !!(user?.provider?.is_admin || user?.provider?.payment_details_enabled);

/**
 * WST-878: pull the first human-readable message out of a DRF error body, so a
 * rejected save can say WHICH field the backend refused instead of a blanket
 * "contact support". DRF answers with {field: ["message"]} (or a bare string);
 * anything else - a network failure, an HTML error page - yields undefined and
 * the caller keeps its own wording.
 */
export const firstServerErrorText = (error?: any): string | undefined => {
  const data = error?.response?.data;
  if (!data) return undefined;
  const first = typeof data === 'string' ? data : Object.values(data)[0];
  const text = Array.isArray(first) ? first[0] : first;
  return typeof text === 'string' && text ? text : undefined;
};

// WST-891: the interval normalizer and the start-date rule moved into the
// service-plan form model (buildEditPayload gates on the latter, and this
// module imports that one — the reverse would be a cycle); both are re-exported
// here so _requests.ts, the components and this module's own tests keep their
// import path. normalizePlanIntervals' count formula mirrors the billing pair
// buildEditPayload (in that same module) sends — keep the two consistent.
export { canEditStartDate, normalizePlanIntervals } from './service-plan-form/planForm';

const formatChangeValue = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'start_date' || field === 'effective_from') {
    const m = moment(String(value), 'YYYY-MM-DD');
    if (m.isValid()) return m.format('LL');
  }
  return String(value);
};

export const describePlanChange = (entry: PlanChangeEntry): string => {
  const { field, service } = splitChangeField(entry.field);
  // An unknown field keeps its raw key, brackets and all, so nothing is hidden.
  const known = PLAN_CHANGE_FIELD_LABELS[field];
  const label = known ? (service ? `${known} — ${service}` : known) : entry.field;
  return `${label}: ${formatChangeValue(field, entry.from)} → ${formatChangeValue(field, entry.to)}`;
};
